import React, { useState } from "react";
import { X, IndianRupee, Send, Loader2, ScanLine } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { toast } from "sonner";

// -----------------------------------------------------------------------------
// SellFromScan — pops up after the built-in scanner returns a find2near tag.
// Shows the tag summary and lets the shopkeeper issue a P2P bill in one tap.
// The counterparty can be identified by NVN / mobile / email; if none match a
// registered user we fall back to an off-ledger name so the sale still posts
// on the issuer's books via /erp/bill/issue.
// -----------------------------------------------------------------------------
export default function SellFromScan({ open, tag, onClose, onIssued }) {
  const [cp, setCp] = useState({ nvn: "", mobile: "", email: "", name: "" });
  const [busy, setBusy] = useState(false);

  if (!open || !tag) return null;

  const grand = Math.round(tag?.totals?.grand || tag?.totals?.grand_total || 0);
  const qty   = Math.max(1, parseInt(tag.quantity || 1, 10));
  const unitPricePaise = Math.round((grand / qty) * 100);

  const sell = async () => {
    if (!cp.nvn && !cp.mobile && !cp.email && !cp.name.trim()) {
      toast.error("Enter buyer NVN, mobile, email, or a name");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        counterparty_nvn:    cp.nvn.trim() || undefined,
        counterparty_mobile: cp.mobile.trim() || undefined,
        counterparty_email:  cp.email.trim() || undefined,
        counterparty_name:   cp.name.trim() || undefined,
        payment_terms: "on_receipt",
        payment_account: "debtors",
        note: `Sold via scanned tag ${tag.item_code || ""}`.trim(),
        lines: [{
          description: `${tag.item_code || "Item"} · ${tag.purity || tag.metal || ""} · ${tag.net_weight ?? tag.gold_weight ?? ""}${tag.unit || ""}`.trim(),
          quantity: qty,
          unit_price_paise: unitPricePaise,
          tax_pct: parseFloat(tag.gst_pct || 0),
        }],
      };
      const r = await api.post("/erp/bill/issue", payload);
      toast.success(r.data.counterparty_notified_in_ledger
        ? "Bill issued · both ledgers updated"
        : "Bill issued (off-ledger)");
      onIssued?.(r.data);
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to issue bill");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[105] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="sell-from-scan">
      <div className="bg-white rounded-3xl w-full max-w-md overflow-hidden shadow-2xl">
        <div className="flex items-center justify-between px-5 py-3 border-b border-stone-200 bg-gradient-to-r from-emerald-50 to-white">
          <div className="flex items-center gap-2">
            <ScanLine className="text-emerald-600" size={16} />
            <div className="font-black text-stone-900">Sell scanned tag</div>
          </div>
          <button onClick={onClose} className="text-stone-500 hover:text-stone-900" data-testid="sell-close"><X size={18} /></button>
        </div>

        <div className="p-5 space-y-3">
          <div className="rounded-2xl bg-amber-50 border border-amber-200 p-3">
            <div className="text-[10px] uppercase tracking-widest font-bold text-amber-700">Scanned tag</div>
            <div className="font-black font-mono text-stone-900">{tag.item_code || "—"} · {tag.purity || tag.metal || ""}</div>
            <div className="grid grid-cols-2 gap-1 mt-2 text-xs text-stone-700">
              <div>Net: <span className="font-mono">{tag.net_weight ?? "—"} {tag.unit}</span></div>
              <div>Gold: <span className="font-mono">{tag.gold_weight ?? "—"} {tag.unit}</span></div>
              <div>Qty: <span className="font-mono">{qty}</span></div>
              <div>Rate: <span className="font-mono">₹{parseFloat(tag.cost_per_unit || 0).toLocaleString("en-IN")}/{tag.unit}</span></div>
            </div>
            <div className="mt-2 flex items-baseline justify-between text-sm">
              <span className="font-bold text-stone-600">Total (incl. GST)</span>
              <span className="font-mono font-black text-emerald-700 text-lg inline-flex items-center"><IndianRupee size={13} />{grand.toLocaleString("en-IN")}</span>
            </div>
          </div>

          <div className="text-[10px] uppercase tracking-widest font-bold text-stone-500">Buyer (any one)</div>
          <div className="grid grid-cols-2 gap-2">
            <Input placeholder="NVN (12 digits)" value={cp.nvn} onChange={(e) => setCp({ ...cp, nvn: e.target.value })} className="rounded-xl font-mono text-sm" data-testid="sell-nvn" />
            <Input placeholder="Mobile" value={cp.mobile} onChange={(e) => setCp({ ...cp, mobile: e.target.value })} className="rounded-xl font-mono text-sm" data-testid="sell-mobile" />
            <Input placeholder="Email" value={cp.email} onChange={(e) => setCp({ ...cp, email: e.target.value })} className="rounded-xl text-sm col-span-2" data-testid="sell-email" />
            <Input placeholder="Off-ledger buyer name" value={cp.name} onChange={(e) => setCp({ ...cp, name: e.target.value })} className="rounded-xl text-sm col-span-2" data-testid="sell-name" />
          </div>
          <div className="text-[10px] text-stone-500">If none match a registered user, the bill posts on your books as off-ledger.</div>

          <Button type="button" onClick={sell} disabled={busy} className="w-full rounded-full h-10 bg-emerald-600 hover:bg-emerald-700 font-black" data-testid="sell-confirm">
            {busy ? <Loader2 className="animate-spin mr-2" size={14} /> : <Send size={14} className="mr-2" />}
            Issue bill · ₹{grand.toLocaleString("en-IN")}
          </Button>
        </div>
      </div>
    </div>
  );
}
